import { useLazyQuery } from '@apollo/client';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import {
  AccessEvent,
  AccessEventsDateRange,
  AccessEventsFilter,
  MobileOpCode,
  Query,
  QueryGetAccessEventsForTenantArgs
} from 'graphql/generated/graphqlTypes';
import { GET_ACCESS_EVENTS_FOR_TENANT } from 'graphql/queries';
import { BaseHookProps } from 'hooks/shared/types';
import { useEffect, useState } from 'react';

dayjs.extend(utc);

interface Props extends BaseHookProps {
  fetchNextData: boolean;
  filter: null | AccessEventsFilter;
}

const getDateRange = (dateRange: AccessEventsDateRange) => ({
  rangeStart: dayjs(dateRange.rangeStart).second(0).millisecond(0).utc().format(),
  rangeEnd: dayjs(dateRange.rangeEnd).second(0).millisecond(0).subtract(1, 'millisecond').utc().format()
});

const useAccessEvents = ({ handleFetchError, fetchNextData, filter }: Props) => {
  const [accessEvent, setAccessEvent] = useState<AccessEvent[]>([]);
  const [nextToken, setNextToken] = useState<string | null>(null);
  const [getAccessEvents, { loading }] = useLazyQuery<
    Pick<Query, 'getAccessEventsForTenant'>,
    QueryGetAccessEventsForTenantArgs
  >(GET_ACCESS_EVENTS_FOR_TENANT, {
    onCompleted: (data) => {
      if (data.getAccessEventsForTenant.nextToken !== nextToken)
        setAccessEvent([...accessEvent, ...data.getAccessEventsForTenant.items]);
      else setAccessEvent(data.getAccessEventsForTenant.items);
      setNextToken(data.getAccessEventsForTenant.nextToken || null);
    },
    onError: () => {
      handleFetchError(`Error while fetching Access Events`);
      setNextToken(null);
    },
    fetchPolicy: 'network-only'
  });

  useEffect(() => {
    setAccessEvent([]);
  }, [filter]);

  useEffect(() => {
    if (filter) {
      const { ...accessEventFiltersWithoutIsFilterApplied } = filter;
      getAccessEvents({
        variables: {
          filter: {
            ...accessEventFiltersWithoutIsFilterApplied,
            actions: accessEventFiltersWithoutIsFilterApplied.actions
              ? [...accessEventFiltersWithoutIsFilterApplied.actions]
              : [MobileOpCode.AccessDenied, MobileOpCode.AccessGranted],
            dateRange: filter.dateRange ? getDateRange(filter.dateRange) : undefined
          }
        }
      });
    } else {
      getAccessEvents({
        variables: { filter: { actions: [MobileOpCode.AccessDenied, MobileOpCode.AccessGranted] } }
      });
    }
  }, [filter, getAccessEvents]);

  useEffect(() => {
    if (fetchNextData && nextToken) {
      if (filter) {
        const { ...accessEventFiltersWithoutIsFilterApplied } = filter;
        getAccessEvents({
          variables: {
            nextToken,
            filter: {
              ...accessEventFiltersWithoutIsFilterApplied,
              actions: accessEventFiltersWithoutIsFilterApplied.actions
                ? [...accessEventFiltersWithoutIsFilterApplied.actions]
                : [MobileOpCode.AccessDenied, MobileOpCode.AccessGranted],
              dateRange: filter.dateRange ? getDateRange(filter.dateRange) : undefined
            }
          }
        });
      } else {
        getAccessEvents({
          variables: {
            nextToken,
            filter: {
              actions: [MobileOpCode.AccessDenied, MobileOpCode.AccessGranted]
            }
          }
        });
      }
    }
  }, [fetchNextData, filter, getAccessEvents, nextToken]);

  return {
    accessEvents: accessEvent,
    isNextDataToDownload: nextToken !== null && !loading,
    loading
  };
};

export default useAccessEvents;
